export const jobsArticles = [
    {
        id: 'jobs.overview',
        title: 'Jobs Overview',
        category: 'Jobs',
        order: 1,
        summary: 'Managing construction jobs and projects',
        content: `
The Jobs page is where you create and manage all your construction projects. Each job represents a distinct project with its own bill of quantities, orders, documents, and financial tracking.

**Job Structure:**

- **Job Number** - Unique identifier for the job
- **Job Name** - Descriptive name for the project
- **Client** - The customer for whom the work is performed
- **Status** - Current state of the job. Statuses are your own words (Residential, Fitout, Active, On Hold, Completed…); a few are special — *Template*, *Office Use*, *Internal*, *Status Name* and *Archived* hide the job from the list and can only be set by an owner, admin or manager.

**Key Features:**
- Create and edit job details
- View job financial summary
- Manage contract claims
- Track job documents

**Who can do what:** anyone except a Viewer creates and edits jobs, notes and job variables (viewers see them read-only). Renaming a status across the workspace, or setting one of the hidden statuses, needs an owner, admin or manager. Deleting a job needs an owner or admin — and is refused while anything still points at the job (orders, transactions, documents, claims, projects, notes): archive it instead. A deleted job sits in the Recycle Bin for 30 days and can be restored with its bill and stage claims.
`,
        expandableSections: [
            {
                title: 'Job Grid Features',
                content: `
The jobs grid provides powerful features for managing your jobs:

**Sorting & Filtering:**
- Click column headers to sort
- Use the filter row to search specific columns
- Apply saved views for common filter combinations

**Context Menu:**
Right-click a job to access:
- Edit job details
- View BOQ
- Create order
- View documents
- Archive/Delete
        `,
            },
        ],
        keywords: ['jobs', 'projects', 'job list', 'job management'],
        route: '/jobs',
        relatedArticles: ['jobs.create', 'jobs.claims', 'boq.overview'],
        screenshots: [
            {
                src: 'jobs/jobs-overview.png',
                alt: 'Jobs list view',
                caption: 'The Jobs page shows all your projects with key details',
            },
            {
                src: 'jobs/jobs-details.png',
                alt: 'Job details panel',
                caption: 'View and edit comprehensive job information',
            },
        ],
    },
    {
        id: 'jobs.create',
        title: 'Creating a New Job',
        category: 'Jobs',
        order: 2,
        summary: 'How to create and configure a new job',
        content: `
Creating a new job establishes a project record that will contain your bill of quantities, orders, and financial data.

**Required Information:**

- **Job Number** - A unique identifier you type (up to 20 characters; spaces around it are trimmed and two numbers that differ only by case are treated as the same)
- **Job Name** - A descriptive name for the project
**Optional Information:**

- Client details and contact information
- Site address
- Contract value and dates
- Project manager and sales person
    `,
        expandableSections: [
            {
                title: 'Step-by-Step Guide',
                content: `
1. Navigate to **Jobs** from the sidebar
2. Click the **Add Job** button in the toolbar
3. Fill in the required fields:
   - Job Number (unique)
   - Job Name
4. Add optional details as needed:
   - Client information
   - Site address
   - Contract details
5. Click **Save** to create the job

The new job will appear in the grid and be available for selection in other areas.
        `,
            },
            {
                title: 'Job Numbering',
                content: `
**Manual numbers:** you enter the job number. It must be unique in your workspace — Costed refuses a duplicate by name, ignoring case and surrounding spaces — and should follow your company's convention (Databuild-migrated jobs keep their original numbers). Numbers that are all digits sort numerically in the list; others sort alphabetically after them.

**Copying a job:** right-click a job → Copy. The copy carries the client, site, contract figures and (if you tick it) the whole bill with its prices, codes and suppliers; you choose the new number and may give it a name. Orders are never copied.
`,
            },
            {
                title: 'Document folders for the new job',
                content: `
A new job starts with no document folders. Three ways to get them:

- **Ask Claude** — upload the job's files, then ask Ask Costed or Claude to set up the
  folders (for example "like J2601") and file the uploads. One approval creates the
  whole tree. See Help → Documents → "Folders built by Claude".
- **Folder template** — Documents → folder rail → apply a saved template to the job.
- **By hand** — right-click in the folder rail → New folder.
`,
            },
        ],
        keywords: ['create job', 'new job', 'add job', 'job setup', 'job folders', 'folder structure'],
        route: '/jobs',
        relatedArticles: ['jobs.overview', 'jobs.edit', 'boq.overview'],
        screenshots: [
            {
                src: 'jobs/jobs-create-modal.png',
                alt: 'New Job form',
                caption: 'The New Job form — only the Job No is required to get started',
            },
        ],
    },
    {
        id: 'jobs.edit',
        title: 'Editing Job Details',
        category: 'Jobs',
        order: 3,
        summary: 'How to modify existing job information',
        content: `
You can edit job details at any time to update project information, client details, or contract values.

**Editable Fields:**

- Job name and description
- Client and contact information
- Site address
- Contract value and dates
- Status (Active, Complete, On Hold)
- Assigned staff

**Note:** The Job Number cannot be changed after creation as it's used as a unique identifier throughout the system.
    `,
        expandableSections: [
            {
                title: 'Editing Methods',
                content: `
**From the Grid:**
1. Double-click the job row to open the edit form
2. Or right-click and select "Edit" from the context menu

**Inline Editing:**
Some fields can be edited directly in the grid:
- Click a cell to edit
- Press Enter to save
- Press Escape to cancel
        `,
            },
        ],
        keywords: ['edit job', 'modify job', 'update job', 'change job'],
        route: '/jobs',
        relatedArticles: ['jobs.overview', 'jobs.create'],
    },
    {
        id: 'jobs.claims',
        title: 'Contract Claims',
        category: 'Jobs',
        order: 4,
        summary: 'Managing progress claims and client invoicing',
        content: `
The Contract Claims tab is the job's money page: the contract summary, the claim schedule, variations, posted claims and receipts, all on one screen.

**Contract summary** shows the contract (ex-tax, tax, inclusive), authorised variations, the **amended contract**, what has been claimed, what has been received and what is outstanding — every figure at your workspace's tax rate and name, rounded to the cent.

**Claim schedule** — the stages from the claim template (Deposit, Base, Frame…). Each stage is a percentage of the amended contract. Mark a stage **Ready**, then **Post** it: that creates the claim, authorises it and (when the ledger is on) posts it to the ledger. A posted stage is **Outstanding** until receipts cover it, then **Paid**.

**Variations** — additions and omissions with their own approval workflow; once authorised they change the amended contract and the value of every stage not yet posted.

**Receipts** — record what the client paid and allocate it to a stage.

Only owners, admins and managers can post, unpost, authorise or record receipts.
    `,
        expandableSections: [
            {
                title: 'Creating a Claim',
                content: `
1. Open the job → **Contract Claims** tab
2. If the schedule is empty, choose a **claim template** (stages must add up to 100 %)
3. Tick **Ready** on the stage that has been reached
4. Click **Post** and confirm

The stage shows the amount ex-tax and tax-inclusive; the posted claim appears under Claims, in Accounting → Outstanding Claims and in the client portal.
        `,
            },
            {
                title: 'Claim Templates',
                content: `
Claim templates define the stages and percentages for progress claims:

- **Deposit** - Initial payment before work begins
- **Base Stage** - Foundation complete
- **Frame Stage** - Structural frame complete
- **Lockup Stage** - External envelope complete
- **Fixing Stage** - Internal finishing
- **Completion** - Project handover

Configure templates in Settings > Accounting.
        `,
            },
        ],
        keywords: ['claims', 'progress claims', 'invoice', 'billing', 'variations'],
        route: '/jobs',
        relatedArticles: ['jobs.overview', 'accounting.receipts'],
        screenshots: [
            {
                src: 'jobs/contract-claims-modal.png',
                alt: 'Contract claims dialog',
                caption: 'Create and manage progress claims for client invoicing',
            },
        ],
    },
    {
        id: 'jobs.allowances',
        title: 'Allowances (PS & PC)',
        category: 'Jobs',
        order: 5,
        summary: 'Managing Provisional Sums and Prime Cost allowances on a job',
        content: `
The Allowances tab lists the Provisional Sum (PS) and Prime Cost (PC) lines for a job. Use it to set client pricing margins, track actual costs against each allowance, and settle each one to a firm price when the real cost is known.

**Adding Allowances:**

- Click **Add Allowance** to create a new PS or PC line — enter a description, amount (ex GST), cost centre, and optional notes
- Or mark an existing BOQ line: right-click a bill item in the BOQ grid → **Mark as Provisional Sum (PS)** or **Mark as Prime Cost (PC)**

**Client Pricing:**

The **Default margin for this job** applies to every allowance unless overridden per line (Client price = cost ÷ (1 − margin/100)). Click the pencil/dots button in the **Eff %** column to set or clear a per-line override.

**Client PDF:**

Use **Preview Estimate** and **Download Estimate PDF** to produce the client-facing allowances schedule (client prices, with GST). The **Notes for client (overall)** box prints above the list; each allowance can also carry its own **Notes for Client** in its Details panel.
    `,
        expandableSections: [
            {
                title: 'Reading the Allowances Table',
                content: `
**Summary cards** across the top show: number of allowances, **Total Cost** (internal, ex margin), **Total Client** (what appears on the quote/PDF), **Total Actual** (recorded to date), and **Net Variance** (from reconciled rows only).

**Columns:**
- **Type** - PS or PC badge
- **Qty / CC / Cost** - quantity, cost centre, and internal cost estimate
- **Eff %** - effective margin (a pencil icon marks a per-line override of the job default)
- **Client** - the marked-up client price
- **Actual / Variance** - recorded actual cost and its difference vs the estimate
- **Status** - \`estimate\` (no actuals yet), \`reconciled\` (actuals recorded), or \`converted\` (settled to a firm price)
        `,
            },
            {
                title: 'Tracking Actual Costs',
                content: `
Click **Details** on a row to expand its reconciliation panel. Actual cost is built from three sources:

**Allocated BOQ Items** - link bill items to the allowance by right-clicking them in the BOQ grid → **Allocate to Allowance**. Each shows its contribution and whether it is *planned* or *ordered*. Use the pencil button to add client-facing notes per line, or the ✕ to remove an allocation (the bill item stays in the BOQ).

**Allocated Labour** - timesheet entries tagged to this allowance via the Allowance dropdown on the Timesheets page, with hours, rate, and contribution.

**Manual Reconciliations** - use the **Record Actual Cost** form to log an amount (ex GST) with a source (Manual, Invoice, Order, or Variation) and notes. Entries can be deleted until the allowance is converted.
        `,
            },
            {
                title: 'Converting to a Firm Price',
                content: `
When the real cost is known, click **Convert** on the row:

1. Enter the **Final Cost Amount (ex GST)** — the dialog shows the delta vs the estimate and a client price preview
2. Leave **Create a Variation Order for the delta** ticked to formally adjust the contract value
3. Click **Convert**

**The variation rule is asymmetric:**
- **Over allowance** — a debit Variation Order is raised for the cost overrun *plus* project margin on the overrun
- **Under allowance** — a credit Variation Order passes the cost saving back to the client; project margin is *not* credited back

Converted rows show the original cost, final settled amount, and delta, with a **Variation PDF** button when a VO was created.
        `,
            },
        ],
        keywords: ['allowances', 'provisional sum', 'PS', 'prime cost', 'PC', 'margin', 'variation order', 'convert', 'reconciliation', 'estimate PDF'],
        route: '/jobs',
        relatedArticles: ['jobs.overview', 'jobs.claims', 'jobs.selections', 'boq.overview'],
        screenshots: [
            {
                src: 'jobs/jobs-allowances.png',
                alt: 'Allowances tab on a job',
                caption: 'The Allowances tab — PS/PC lines with cost, client price, actuals, and variance',
            },
        ],
    },
    {
        id: 'jobs.selections',
        title: 'Client Selections',
        category: 'Jobs',
        order: 6,
        summary: 'Offering clients product choices against PS/PC allowances',
        content: `
The Selections tab turns a job's allowances into client choices — for example brick range, tapware, or floor coverings. You offer options against each allowance; the client picks in the portal, and any upgrade cost is settled via the Allowances tab's **Convert to firm price**.

**Creating a Selection:**

1. In **Make an allowance a client selection**, choose an allowance from the dropdown and click **Add selection** (items must first be marked PS/PC on the Allowances tab)
2. Set **Required**, an optional **Deadline**, and the **Pricing** mode — *Flat total* or *Per-unit × qty* (the quantity comes from the BOQ and is edited there)
3. Add at least two options for the client to choose from

A badge at the top tracks progress, e.g. **2 of 5 required selected**. Each card shows the included allowance amount and, once chosen, who selected which option and when — staff can also set or clear the client's pick on their behalf.
    `,
        expandableSections: [
            {
                title: 'Adding and Editing Options',
                content: `
Each option has:

- **Option name** (required) and an optional rich-text **Description** shown to the client
- **Price (ex GST)** for flat pricing, or **Unit rate (ex GST)** for per-unit × qty pricing
- **Image URL** (optional) — must start with http:// or https://
- **Standard inclusion (no upgrade cost)** — tick for the included/base option
- **Add-on costs** — extra labelled amounts (one-off for flat pricing, $/unit for per-unit, e.g. Laying)

The list shows each option's price and whether it is **within allowance** or a **+$ upgrade** over the included allowance. Use the buttons on each option to set it as the client's choice, edit it, or remove it.
        `,
            },
            {
                title: 'Sections and Standard Inclusions',
                content: `
Group selections under headings (e.g. Wet Areas) with **Add section**:

- Rename a section by typing directly in its heading; use the buttons to **duplicate** (with its selections) or **delete** it (its selections become ungrouped)
- **Add standard inclusion** adds a plain text line shown to the client (e.g. "Chrome mixer tapware throughout")
- Move a selection between sections with the section dropdown on its card
        `,
            },
            {
                title: 'Selection Templates',
                content: `
Reusable inclusion/selection sets (e.g. Classic Inclusions, Single Storey, Duplex) save re-typing the same spec on every job.

**Applying:** pick a template from the **Templates** dropdown and click **Apply** — it adds the template's sections and selections to the job.

**Managing:** click **Manage templates** to open the Selection Templates library. Create, edit, duplicate, or delete templates (deleting one does not affect jobs it was already applied to). In the editor, build sections of **Inclusion** (text) and **Selection** (priced) items — selections carry a pricing mode, default qty, allowance estimate, cost centre code, Required flag, and their options with add-ons.
        `,
            },
        ],
        keywords: ['selections', 'client selections', 'colour choices', 'options', 'selection templates', 'standard inclusion', 'upgrade', 'allowance', 'deadline', 'portal'],
        route: '/jobs',
        relatedArticles: ['jobs.allowances', 'jobs.overview', 'boq.overview'],
        screenshots: [
            {
                src: 'jobs/jobs-selections.png',
                alt: 'Selections tab on a job',
                caption: 'The Selections tab — allowances offered as client choices with options and sections',
            },
        ],
    },
];
